import { Col, Row } from 'react-bootstrap';
import { Dayts, DaytsFormatEnum, createNotification } from '@/utils';
import DeferredFileUpload, { DeferredFileUploadHandle } from '@/components/FileUpload/deferred-file-upload';
import GenericFileUpload, { FileUploadStage, GenericFile, GenericFileType } from '@/components/FileUpload/new';

import { DocumentUploadInfo } from './DocumentUploadInfo';
import HasPermission from '@/components/HasPermission';
import Toast from '@/models/Toast';
import { createTripDocumentPresignedUrl } from '@/features/Trips/api/services/createTripDocumentPresignedUploadUrl';
import { useTripDocuments } from './useTripDocuments';

interface TripDocumentUploadSectionProps {
  tripId: string;
  mode: 'editing' | 'creating';
  fileUploadRef?: React.RefObject<DeferredFileUploadHandle>;
}

/**
 * Reusable component for trip document upload and management
 * Handles both editing mode (with GenericFileUpload) and creating mode (with DeferredFileUpload)
 */
export const TripDocumentUploadSection = ({ tripId, mode, fileUploadRef }: TripDocumentUploadSectionProps) => {
  const { documents, deletedDocuments, recentlyUploadedIds, handleDeleteDocument, handleUploadSuccess } = useTripDocuments({
    tripId,
    mode,
  });

  if (mode === 'creating' && HasPermission.check('allowUploadTripDocument')) {
    return (
      <div className="TripFormUpload">
        <Col>
          <DeferredFileUpload ref={fileUploadRef} options={{ multiple: true, showFiles: true }} />
        </Col>
      </div>
    );
  }

  return (
    <>
      <HasPermission name="allowUploadTripDocument">
        <div className="TripFormUpload">
          <Col>
            <GenericFileUpload
              endpoint={async (file: GenericFileType) => {
                try {
                  const payload = {
                    filename: file.file.name,
                    md5: file.md5,
                    fileSize: file.file.size,
                    tripId: tripId,
                  };
                  const res = await createTripDocumentPresignedUrl(payload);
                  createNotification('Sucessfully Uploaded Document', Toast.Type.SUCCESS, 'Trip');
                  return [res?.url, res?.contentType];
                } catch (e) {
                  console.error('Error Uploading Document', e);
                  createNotification('Could Not Upload Document', Toast.Type.DANGER, 'Trip');
                }
              }}
              options={{
                showFiles: true,
                multiple: true,
                hideSuccessful: true,
              }}
              locale={{
                Upload: 'UPLOAD FILES',
              }}
              onError={async () => console.error('Error uploading trip document')}
              onSuccess={async () => handleUploadSuccess(tripId)}
            />
          </Col>
        </div>
      </HasPermission>
      {documents?.map((document) => {
        const isRecentlyUploaded = recentlyUploadedIds.has(document.id);
        return (
          <div key={document.id}>
            <Row>
              <GenericFile
                name={document.filename}
                preview={document.fileLocation}
                onDelete={() => handleDeleteDocument(document.id)}
                stage={isRecentlyUploaded ? FileUploadStage.Done : FileUploadStage.Uploaded}
                progress={isRecentlyUploaded ? 100 : undefined}
              >
                <DocumentUploadInfo>
                  <div>
                    <span className="fw-semibold">Uploaded</span> by {document?.createdByFirstName} {document?.createdByLastName}{' '}
                    {new Dayts(document?.createdAt).formatAs(DaytsFormatEnum.DATETIME_FE_ACTIVITY)}
                  </div>
                </DocumentUploadInfo>
              </GenericFile>
            </Row>
          </div>
        );
      })}
      <HasPermission name="allowViewDeletedTripDocument">
        {deletedDocuments.length > 0 && (
          <>
            <div className="w-100 d-flex justify-content-center align-items-center">
              <div className="w-50" style={{ height: '3px', backgroundColor: '#D9D9D9', borderRadius: '4px' }} />
            </div>
            {deletedDocuments?.map((document) => (
              <div key={document.id}>
                <Row>
                  <GenericFile name={document.filename} preview={document.fileLocation} stage={FileUploadStage.Deleted}>
                    <DocumentUploadInfo>
                      <div>
                        <span className="fw-semibold">Uploaded</span> by {document?.createdByFirstName} {document?.createdByLastName}{' '}
                        {new Dayts(document?.createdAt).formatAs(DaytsFormatEnum.DATETIME_FE_ACTIVITY)}
                      </div>
                      <div>
                        <span className="fw-semibold">Deleted</span> by {document?.deletedByFirstName} {document?.deletedByLastName}{' '}
                        {new Dayts(document?.deletedAt).formatAs(DaytsFormatEnum.DATETIME_FE_ACTIVITY)}
                      </div>
                    </DocumentUploadInfo>
                  </GenericFile>
                </Row>
              </div>
            ))}
          </>
        )}
      </HasPermission>
    </>
  );
};
